import { useEffect, useState } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { MapPin, X, ArrowRight, Globe, Navigation, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGeoLocation, findNearestRegion } from "@/hooks/use-geo-location";
import { REGIONS } from "@/lib/locations";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const GeoLocationBanner = () => {
  // Delay mounting by 4s so it never competes with LCP / initial paint
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 4000);
    return () => clearTimeout(t);
  }, []);

  if (!mounted) return null;
  return <GeoLocationBannerInner />;
};

const GeoLocationBannerInner = () => {
  const { status, detectedRegion, detectedRegionName, detectedCity, dismiss } = useGeoLocation();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  // Don't show on region/location pages — user already picked their area
  const isOnLocationPage = routerLocation.pathname.startsWith("/locations/");
  const isHomePage = routerLocation.pathname === "/";

  useEffect(() => {
    if (status === "detected" && detectedRegion && isHomePage) {
      dismiss();
      navigate(`/locations/${detectedRegion}`);
    }
  }, [status, detectedRegion, isHomePage, navigate, dismiss]);

  useEffect(() => {
    // Prevent mobile scroll from triggering this by checking if it's a touch device or width
    const isMobile = window.innerWidth <= 768;
    
    const handleMouseLeave = (e: MouseEvent) => {
      if (isMobile) return;
      if (e.clientY <= 0 && status !== "detected" && status !== "outside" && status !== "dismissed" && !isOnLocationPage && !showExitIntent) {
        setShowExitIntent(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [status, isOnLocationPage, showExitIntent]);

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const nearest = findNearestRegion(latitude, longitude);
        if (nearest && REGIONS[nearest]) {
          dismiss();
          setShowExitIntent(false);
          navigate(`/locations/${nearest}`);
          toast.success(`Found you! Showing service areas near ${REGIONS[nearest].name}.`);
        } else {
          toast.error("You appear to be outside our current service areas.");
        }
        setIsLocating(false);
      },
      () => {
        toast.error("Unable to retrieve your location.");
        setIsLocating(false);
      }
    );
  };

  if (isOnLocationPage || status === "dismissed" || status === "detecting") {
    return null;
  }

  if (status === "outside" || showExitIntent) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) { dismiss(); setShowExitIntent(false); } }}>
        <DialogContent className="sm:max-w-md w-[95vw] rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
          <div className="bg-primary/10 p-6 pb-4 flex flex-col items-center text-center">
            <div className="h-12 w-12 bg-primary/20 rounded-full flex items-center justify-center mb-3">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">Select Your Region</DialogTitle>
            <DialogDescription className="text-sm mt-2 text-muted-foreground max-w-[280px] mx-auto">
              We couldn't detect your exact location. Please select your service area to see local pricing and availability.
            </DialogDescription>
          </div>
          
          <div className="p-4 bg-background">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto pr-1 pb-2 custom-scrollbar">
              {Object.entries(REGIONS).map(([slug, region]) => (
                <Button
                  key={slug}
                  variant="outline"
                  className="justify-start h-auto py-3 px-4 text-left font-medium border-border hover:border-primary/50 hover:bg-primary/5 transition-all group rounded-xl"
                  onClick={() => {
                    dismiss();
                    navigate(`/locations/${slug}`);
                  }}
                >
                  <MapPin className="h-4 w-4 mr-3 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                  <span className="truncate">{region.name}</span>
                </Button>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-border flex flex-col gap-2 sm:flex-row sm:justify-between items-center">
              <Button variant="outline" onClick={handleLocateMe} disabled={isLocating} className="w-full sm:w-auto text-sm border-primary/20 hover:bg-primary/5 text-primary">
                {isLocating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Navigation className="h-4 w-4 mr-2" />}
                Detect My Location
              </Button>
              <Button variant="ghost" onClick={() => { dismiss(); setShowExitIntent(false); }} className="w-full sm:w-auto text-muted-foreground hover:text-foreground text-sm">
                Continue with default region
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};
